import { store } from '../../app/store';
import { selectAuthAccessToken } from '../../features/auth/store/authSlice';
import { API_ERRORS, resolveApiErrorMessage } from '../constants/apiErrors';

function resolveApiBaseUrl() {
  const apiHostUrl = import.meta.env.VITE_API_URL;
  if (!apiHostUrl) return '/api';

  return `${String(apiHostUrl).replace(/\/$/, '')}/api`;
}

function withBaseUrl(path) {
  const base = resolveApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function resolveAuthToken() {
  return selectAuthAccessToken(store.getState());
}

function buildHeaders(headers, token) {
  const resolved = { ...(headers ?? {}) };
  if (token) resolved.Authorization = `Bearer ${token}`;
  return resolved;
}

async function safeParseJson(response) {
  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  if (!isJson) return null;
  return response.json().catch(() => null);
}

function createNetworkError(cause) {
  const error = new Error(API_ERRORS.NETWORK.message);
  error.code = API_ERRORS.NETWORK.code;
  error.cause = cause;
  return error;
}

function createHttpError(response, payload) {
  const message = payload?.detail || payload?.message || resolveApiErrorMessage(response.status);

  const error = new Error(message);
  error.code =
    response.status === 401 || response.status === 403
      ? API_ERRORS.UNAUTHORIZED.code
      : API_ERRORS.UNKNOWN.code;
  error.status = response.status;
  error.payload = payload;
  return error;
}

async function request(path, { method = 'GET', headers, body } = {}) {
  const token = resolveAuthToken();
  const resolvedHeaders = buildHeaders(headers, token);

  let response;
  try {
    response = await fetch(withBaseUrl(path), {
      method,
      headers: resolvedHeaders,
      body,
    });
  } catch (cause) {
    throw createNetworkError(cause);
  }

  const payload = await safeParseJson(response);

  const ok = response.ok || response.status === 304;
  if (!ok) {
    throw createHttpError(response, payload);
  }

  return payload;
}

export const apiClient = {
  get(path, options) {
    return request(path, {
      ...options,
      method: 'GET',
    });
  },
  postJson(path, data, options) {
    return request(path, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers ?? {}),
      },
      body: JSON.stringify(data),
    });
  },
  patchJson(path, data, options) {
    return request(path, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers ?? {}),
      },
      body: JSON.stringify(data),
    });
  },
  delete(path, options) {
    return request(path, {
      ...options,
      method: 'DELETE',
    });
  },
};
